import {
  Component,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { Observable, concatMap, map, of } from 'rxjs';
import { LotDetailsService } from 'src/app/services/lot-details.service';
import {
  BasisPrice,
  CashPrice,
  Lot,
  LotDetailsResponse,
  MarketType,
  PriceType,
  PriceUnion,
} from '../../lots.model';
import { Store } from '@ngrx/store';
import { LotState } from 'src/app/store/lots-library/lots.reducer';
import { SplitLocationPipe } from 'src/app/pipes/split-location.pipe';
import { UserDetails, UserRoles } from 'src/app/components/login/user.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { NegotiationDetailsDialogComponent } from 'src/app/components/custom/negotiation-details-dialog/negotiation-details-dialog.component';
import { NegotiationActionsService } from 'src/app/services/negotiation-actions.service';
import { LotDataService } from 'src/app/services/lot-data.service';
import {
  fetchData,
  removeRow,
} from 'src/app/store/table-data/table-data.actions';
import { LotsService } from 'src/app/services/lots.service';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { selectUser } from 'src/app/store/user/user.selectors';
import { selectTableSelectedMarket } from 'src/app/store/table-data/table-data.selectors';
import { selectActiveLotData } from 'src/app/store/lots-library/lot.selectors';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JCTradingIndicationComponent } from 'src/app/components/custom/chips/jc-trading-indication/jc-trading-indication.component';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';
import {
  LotActionDialogTemplateComponent,
  DialogType,
} from '../negotiation-details-card/lot-action-dialog-template/lot-action-dialog-template.component';
import { QueryRef } from 'apollo-angular';
import { selectLot } from 'src/app/store/lots-library/lot.actions';

@Component({
  selector: 'app-lot-details-card',
  imports: [
    CommonModule,
    ScrollingModule,
    PriceTypePipe,
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    FontAwesomeModule,
    MatTooltipModule,
    JCTradingIndicationComponent,
  ],
  templateUrl: './lot-details-card.component.html',
  styleUrl: './lot-details-card.component.css',
})
export class LotDetailsCardComponent implements OnInit, OnDestroy {
  @Output() counterClicked = new EventEmitter();
  private analytics = inject(Analytics);
  faEdit = faEdit;
  canEdit = signal<boolean>(false);
  lot$: Observable<Lot>;
  userDetails$: Observable<UserDetails>;
  market$: Observable<MarketType>;
  lotData = signal<Lot>(null);
  userId = signal<string>('');
  // hide counter and accept buttons if negotiation/counter(s) exist(s)
  revokeBtnActive = signal<boolean>(false);
  acceptBtnActive = signal<boolean>(false);
  counterBtnActive = signal<boolean>(false);

  market = signal<MarketType>(null);

  form = this.fb.group({
    lot_id: [''],
    price_type: [''],
    cash_price: this.fb.group({
      amount: [0],
    }),
    basis_price: this.fb.group({
      basisPoints: [0, Validators.required],
      month: ['', Validators.required],
      product_code: ['', Validators.required],
      year: [0, Validators.required],
    }),
    volume_value: [0],
    ship_begin: [''],
    ship_end: [''],
  });

  canNegotiateAndAccept = signal(false);

  dialogConfig = new MatDialogConfig();
  horizontalPosition: MatSnackBarHorizontalPosition = 'center';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  constructor(
    private store: Store<{
      activeLot: LotState;
      user: { user: UserDetails };
      lotTableData: TableDataState;
    }>,
    private negotiationsActionsService: NegotiationActionsService,
    private lotDataService: LotDataService,
    private lotsService: LotsService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private injector: Injector
  ) {
    this.userDetails$ = this.store.select(selectUser).pipe(
      map((user) => {
        if (user) {
          this.userId.set(user._id);
          this.canNegotiateAndAccept.set(user.role !== UserRoles.Farmer);
        }
        return user;
      })
    );
  }

  ngOnInit(): void {
    this.market$ = this.store.select(selectTableSelectedMarket).pipe(
      map((selectedMarket) => {
        this.market.set(selectedMarket);
        return selectedMarket;
      })
    );

    this.lot$ = this.store.select(selectActiveLotData).pipe(
      map((res) => {
        this.revokeBtnActive.set(res.negotiationDetails.length === 0);
        this.acceptBtnActive.set(res.negotiationDetails.length === 0);

        /* - hide revoke/cancel btn if user is creater of lot
          - hide counter & accept btns if there negotiation/counter already exists
        */

        //check lot details for btn visibiltity
        if (res.lot._id !== 'none selected') {
          this.lotData.set(res.lot);
          if (this.userId() === res.lot.user_id) {
            this.counterBtnActive.set(false);
            this.acceptBtnActive.set(false);
          } else {
            this.counterBtnActive.set(true);
            this.acceptBtnActive.set(true);
          }
        }
        this.canEdit.set(
          res.lot.user_id === this.userId() &&
            res.negotiationDetails.length === 0
        );

        // check negotiations for btn visibility
        if (res.negotiationDetails.length) {
          if (
            res.negotiationDetails[0].counters[0].originator_id ===
            this.userId()
          ) {
            // if last counter was created by user
            this.revokeBtnActive.set(true);
          } else {
            this.revokeBtnActive.set(false);
            this.acceptBtnActive.set(false);
          }

          if (res.negotiationDetails[0].counters.length) {
            this.acceptBtnActive.set(false);
            this.counterBtnActive.set(false);
          }
        }
        return res.lot;
      })
    );
  }

  editLot() {
    this.form.patchValue(
      {
        lot_id: this.lotData()._id,
        price_type: this.lotsService.isCashPrice(this.lotData().price)
          ? PriceType.Cash
          : PriceType.Basis,
        ...(this.lotsService.isCashPrice(this.lotData().price)
          ? {
              cash_price: { ...(this.lotData().price as CashPrice) },
              basis_price: null,
            }
          : {
              basis_price: { ...(this.lotData().price as BasisPrice) },
              cash_price: null,
            }),
        volume_value: this.lotData().volume_value || 0,
        ship_begin: this.lotData().ship_begin || '',
        ship_end: this.lotData().ship_end || '',
      },
      { emitEvent: false }
    );

    const counterDialogRef = this.dialog.open(
      LotActionDialogTemplateComponent,
      {
        injector: this.injector,
        data: {
          form: this.form,
          lotId: this.lotData()._id,
          dialogTitle: `Edit ${this.lotData().lot_type}`,
          dialogType: DialogType.Edit,
        },
      }
    );

    counterDialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result.confirm) {
          logEvent(this.analytics, LogEventsEnum.lot_edit);
          this.form.patchValue({ ...result.data });
          let values = this.form.value;
          values = this.lotsService.removeProperty(
            values,
            values.price_type === PriceType.Basis ? 'cash_price' : 'basis_price'
          );
          values = this.lotsService.removeProperty(values, 'lot_id');

          this.lotDataService.editLot(this.lotData()._id, values).subscribe({
            next: (result) => {
              // console.log('new nego response', result.data);
              this.form.setErrors({ sameValues: false });
              this.snackBar.open(
                `Updated ${this.lotData().lot_type}`,
                'Close',
                {
                  duration: 3500,
                  panelClass: ['blue-snackbar'],
                }
              );
              this.store.dispatch(
                fetchData({ limit: 40, marketType: this.market() })
              );
            },
            error: (err) => {
              console.log('new negotiation error ', err);
              this.snackBar.open(`Error creating negotiation`, 'Close', {
                duration: 3500,
                panelClass: ['blue-snackbar'],
              });
            },
          });
        }
      },
    });
  }
  accept() {
    this.dialogConfig.data = {
      dialogText: 'Are you sure you want to accept?',
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log("contract dialog close result", result);
        /**
         * 
         TODO : fix this garbage code (use signals)
         */
        let lotId: string;
        this.lot$.subscribe({
          next: (res) => {
            // console.log('accept lot', res);
            lotId = res._id;
          },
        });
        // console.log('lot id', lotId)
        if (result.confirm) {
          this.negotiationsActionsService
            .createContract({
              lot_id: lotId,
            })
            .pipe(
              concatMap((res) => {
                // console.log(res);
                this.store.dispatch(removeRow({ lotId: lotId }));
                return of(res);
              })
            )
            .subscribe({
              next: (result) => {
                // console.log("contract creation result", result)
                this.snackBar.open(
                  `Accepted ${
                    this.lotData().lot_type
                  }. Contract Status Pending`,
                  null,
                  {
                    horizontalPosition: this.horizontalPosition,
                    verticalPosition: this.verticalPosition,
                    duration: 4500,
                    panelClass: ['blue-snackbar'],
                  }
                );

                // this.store.dispatch(removeRow({ lotId: lotId }));
                /*
                TODO : fix market type to fetch market typeof app state 
                */
                this.store.dispatch(
                  fetchData({ limit: 40, marketType: MarketType.Feed })
                );
              },
              error: (err) => {
                // console.log("contract creation error", err)
                this.snackBar.open('Error Creating Contract', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 2000,
                });
              },
            });
        }
      },
    });
  }

  revoke() {
    this.dialogConfig.data = {
      dialogText: `Are you sure you want to remove`,
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        let lotId: string;
        this.lot$.subscribe({ next: (res) => (lotId = res._id) });
        if (result.confirm) {
          this.lotDataService
            .removeLot(lotId)
            .pipe(
              concatMap((res) => {
                // console.log(res);
                this.store.dispatch(
                  removeRow({ lotId: res.data['removeLot']['_id'] })
                );
                return of(res);
              })
            )
            .subscribe({
              next: (result) => {
                // console.log("remove lot result", result)
                this.snackBar.open(
                  `Removed ${result.data['removeLot']['lot_type']}`,
                  'close',
                  {
                    horizontalPosition: this.horizontalPosition,
                    verticalPosition: this.verticalPosition,
                    panelClass: ['blue-snackbar'],
                  }
                );
              },
              error: (err) => {
                // console.log("contract creation error", err)
                this.snackBar.open('Error removing bid/offer', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 2000,
                });
              },
            });
        }
      },
    });
  }
  /**
  emits event to parent component to focus on negotiation
  */
  clickCounter(): void {
    this.counterClicked.emit();
  }

  isCashPrice(price: PriceUnion): price is CashPrice {
    if (price) {
      return (price as CashPrice).amount !== undefined;
    }
    return false;
  }

  isBasisPrice(price: PriceUnion): price is BasisPrice {
    if (price) {
      return (price as BasisPrice).month !== undefined;
    }
    return false;
  }

  ngOnDestroy(): void {}
}
